import { AfterViewInit, Component, computed, effect, EffectRef, ElementRef, inject, Injector, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { BenefitsDescriptionsComponent } from './benefits-descriptions/benefits-descriptions.component';
import { BenefitsService } from '@services/benefits.service';
import { GroupAssignedAmenity } from '@models/interfaces/amenities/AmenityGroupAssigned.interface';
import { BenefitsContact, BenefitsDescriptions } from '@models/types/components/Benefits.type';
import { ButtonType } from '@models/enums/components/Button/ButtonType.enum';
import { BenefitsCarouselComponent } from './benefits-carousel/benefits-carousel.component';
import { MenuCardComponent } from '../../menu/menu-card/menu-card.component';
import { BenefitsContactComponent } from './benefits-contact/benefits-contact.component';
import anime from 'animejs';
import { LayoutService } from '@services/layout.service';
import { BottomSheetService } from '@services/components/bottom-sheet.service';

@Component({
    selector: 'app-benefits-container',
    imports: [
        MenuCardComponent,
        BenefitsCarouselComponent,
        BenefitsDescriptionsComponent,
        BenefitsContactComponent
        // ButtonComponent
    ],
    standalone: true,
    templateUrl: './benefits-container.component.html',
    styleUrl: './benefits-container.component.css'
})
export class BenefitsContainerComponent implements AfterViewInit {

  @ViewChild('benefitsContainer', { static: true, read: ElementRef})
  public benefitsContainer!: ElementRef<HTMLDivElement>;

  button = ButtonType.Pill;

  // SERVICES
  public  readonly benefitsService: BenefitsService                       = inject(BenefitsService);
  private readonly bottomSheet:     BottomSheetService                    = inject(BottomSheetService);
  private readonly injector:        Injector                              = inject(Injector);

  // STATE 
  public  readonly showContainer:  Signal<boolean>                        = this.benefitsService.showContainer;
  private readonly isMobile:       Signal<boolean>                        = inject(LayoutService).isMobile;

  // COMPONENT STATE
  public  readonly hideElement:    WritableSignal<boolean>                = signal(true);
  public           amenityKey:     number | null                          = null;
  
  // SIGNALS
  public  readonly amenities:      Signal<GroupAssignedAmenity[]>         = this.benefitsService.amenities; 
  public  readonly amenity:        WritableSignal<GroupAssignedAmenity|undefined> = signal(undefined);
  public  readonly descriptions:   Signal<BenefitsDescriptions|null>              = computed((): BenefitsDescriptions | null => {

    if(!this.amenity()) return null;

    return {
      publicDescription:      this.amenity()?.publicDescription || ''
    }

  })
  public  readonly carouselData:   Signal<{img: string[], text: string[]} | undefined> = computed((): {img: string[], text: string[]} | undefined => {

   return this.amenity()?.assets?.reduce((acc, asset) => {
    if(asset.isFromCarousel){
      acc.img.push(asset.urlSigned);
      acc.text.push(asset.text);
    }
    return acc;
   }, {img: [] as string[], text:[] as string[]}) ?? undefined;

  })

  public readonly  contact:        Signal<BenefitsContact|null> = computed((): BenefitsContact | null => {
    if(!this.amenity()) return null;

    return {
      emailAddress1: this.amenity()?.emailAddress1  || '',
      phoneNumber1:  this.amenity()?.phoneNumber1   || '',
      emailAddress2: this.amenity()?.emailAddress2  || null,
      phoneNumber2:  this.amenity()?.phoneNumber2   || null
    }

  });

  // EFFECTS
  private toggleContainer!: EffectRef;

  // LIFECYCLE
  public ngAfterViewInit(): void {
    this.toggleContainer = effect((): void => {
      this.toggleContainerContent(this.showContainer());
    }, { injector: this.injector });
  }


  public toggleAmenity(amenityKey: number): void {
    
    // Si seleccionamos la misma, desactivamos
    if(this.amenityKey === amenityKey){
      
      this.amenityKey = null;
      this.amenity.set(undefined);

      // BOTTOM SHEET BEHAVIOR
      if(this.isMobile() && this.bottomSheet.state() === 'full' && this.amenities().length === 1) this.bottomSheet.updateFromComponent('open');

      return; 
    }

    // Si no la seteamos
    this.amenityKey = amenityKey;
    this.amenity.set(this.amenities()[amenityKey] || undefined);    

    // BOTTOM SHEET BEHAVIOR
    if(this.isMobile() && this.bottomSheet.state() === 'open') this.bottomSheet.updateFromComponent('full')
  }

  public hideAmenity(): void{
    this.amenity.set(undefined);
    this.amenityKey = null;
  }

  private toggleContainerContent(show: boolean): void {

    const container: HTMLDivElement | undefined = this.benefitsContainer?.nativeElement;

    if(!container) return;

    if(!show && this.hideElement()) return;

    //open benefits when menu item is selected
    if(show && this.amenities().length && !this.isMobile()) {
      this.amenityKey = 0;
      this.amenity.set(this.amenities()[0]);
    }

    if(show && this.hideElement()) {
      container.style.opacity = '0';
      this.hideElement.set(false);
      requestAnimationFrame((): void => this.animateContainerContent(show));
      return;
    }

    this.animateContainerContent(show);

  }

  private animateContainerContent(show: boolean): void {

    const container: HTMLDivElement | undefined = this.benefitsContainer?.nativeElement;

    if(!container) return;

    const targets: HTMLElement[] = Array.from(container.children) as HTMLElement[];

    anime.timeline({
      begin:    (): void => this.benefitsService.isAnimating.set(true),
      complete: (): void => {
        if(!this.showContainer()) {
          this.hideAmenity();
          this.hideElement.set(true);
        }
        this.benefitsService.isAnimating.set(false);
      }, 
      duration: show ? 300 : 200,
      easing: show ? 'easeInOutQuad' : 'easeInOutCubic',
    })
    .add({
      targets: container,
      opacity: show ? 1 : 0,
    })
    .add({
      targets,
      opacity: show ? 1 : 0,
      easing: 'easeInOutQuad',
      delay: this.amenities().length > 1 ?  anime.stagger(100, {start: 0}) : 0,
    }, this.amenities().length > 1  ? '-=50' : '')

  }


}
